import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Download, FileText, Calendar, Search, Mail, Send, Users, DollarSign, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function Payslips() {
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedPayslips, setGroupedPayslips] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sendingEmail, setSendingEmail] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const isHRAdmin = ['admin', 'hr_manager', 'payroll_officer'].includes(user.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      if (isHRAdmin) {
        // HR Admin - fetch all payslips
        const [payslipsRes, employeesRes] = await Promise.all([
          axios.get(`${API}/hr/payslips`),
          axios.get(`${API}/employees`)
        ]);
        setPayslips(payslipsRes.data);
        setEmployees(employeesRes.data);
      } else if (user.employee_id) {
        // Employee - fetch own payslips
        const response = await axios.get(`${API}/me/payslips`);
        setPayslips(response.data);
        
        // Group payslips by month and year
        const grouped = response.data.reduce((acc, payslip) => {
          const date = new Date(payslip.date);
          const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (!acc[monthYear]) {
            acc[monthYear] = [];
          }
          acc[monthYear].push(payslip);
          return acc;
        }, {});
        
        setGroupedPayslips(grouped);
      } else {
        setPayslips([]);
        setGroupedPayslips({});
      }
    } catch (error) {
      toast.error('Failed to fetch payslips');
    } finally {
      setLoading(false);
    }
  };

  // HR Admin Analytics
  const hrAnalytics = useMemo(() => {
    if (!isHRAdmin) return null;
    
    const totalGross = payslips.reduce((sum, p) => sum + (p.gross_salary || 0), 0);
    const totalNet = payslips.reduce((sum, p) => sum + (p.net_salary || 0), 0);
    const uniqueEmployees = new Set(payslips.map(p => p.employee_id)).size;
    
    return {
      totalPayslips: payslips.length,
      totalGross,
      totalNet,
      uniqueEmployees
    };
  }, [payslips, isHRAdmin]);

  // Filtered payslips for HR Admin
  const filteredHRPayslips = useMemo(() => {
    if (!isHRAdmin) return [];
    
    let filtered = payslips;
    
    // Filter by year and month
    filtered = filtered.filter(payslip => {
      const date = new Date(payslip.date);
      const year = date.getFullYear().toString();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      
      if (selectedYear !== 'all' && year !== selectedYear) return false;
      if (selectedMonth !== 'all' && month !== selectedMonth) return false;
      return true;
    });
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(payslip => {
        return (
          payslip.employee_name?.toLowerCase().includes(query) ||
          payslip.employee_id?.toLowerCase().includes(query) ||
          payslip.employee_email?.toLowerCase().includes(query)
        );
      });
    }
    
    return filtered;
  }, [payslips, selectedYear, selectedMonth, searchQuery, isHRAdmin]);

  // Get unique years for filter
  const availableYears = useMemo(() => {
    const years = new Set(payslips.map(p => new Date(p.date).getFullYear().toString()));
    return [...years].sort().reverse();
  }, [payslips]);

  const handleDownload = async (payslipId, employeeName, month, year) => {
    setDownloadingId(payslipId);
    try {
      const endpoint = isHRAdmin 
        ? `${API}/hr/payslips/${payslipId}/download`
        : `${API}/me/payslips/${payslipId}/download`;
      
      const response = await axios.get(endpoint, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Payslip_${employeeName || 'Employee'}_${month}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Payslip downloaded successfully');
    } catch (error) {
      toast.error('Failed to download payslip');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSendEmail = async (payslipId, employeeName, employeeEmail) => {
    setSendingEmail(payslipId);
    try {
      const res = await axios.post(`${API}/payslips/${payslipId}/send-email`);
      const data = res.data || {};
      if (data.status === 'sent') {
        toast.success(data.message || `Payslip sent to ${employeeEmail}`);
      } else if (data.status === 'disabled') {
        toast.info(data.message || 'Email service is not configured (MOCKED).');
      } else {
        toast.success('Payslip email queued');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(null);
    }
  };

  const formatCurrency = (amount) => {
    return `RM ${(amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMonthYear = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  // HR Admin View
  if (isHRAdmin) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Payslip Management</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Search, download, and send payslips to employees</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <FileText className="h-5 w-5 text-emerald-100" />
              <span className="text-emerald-100 text-sm font-medium">Total Payslips</span>
            </div>
            <p className="text-3xl font-bold">{hrAnalytics?.totalPayslips || 0}</p>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center space-x-2 mb-2">
              <Users className="h-5 w-5 text-blue-500" />
              <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">Employees</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{hrAnalytics?.uniqueEmployees || 0}</p>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="h-5 w-5 text-emerald-500" />
              <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">Total Gross</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(hrAnalytics?.totalGross)}</p>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="h-5 w-5 text-violet-500" />
              <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">Total Net</span>
            </div>
            <p className="text-2xl font-bold text-violet-600">{formatCurrency(hrAnalytics?.totalNet)}</p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search by employee name, ID, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(e.target.value)}
              className="h-10 px-4 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            >
              <option value="all">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="h-10 px-4 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            >
              <option value="all">All Months</option>
              {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Payslips Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Employee Payslips</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">{filteredHRPayslips.length} payslips found</p>
          </div>

          {filteredHRPayslips.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">
                {searchQuery ? 'No payslips found matching your search' : 'No payslips available'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Employee</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Period</th>
                    <th className="text-right px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Gross Salary</th>
                    <th className="text-right px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Deductions</th>
                    <th className="text-right px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Net Salary</th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {filteredHRPayslips.map((payslip) => {
                    const date = new Date(payslip.date);
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear().toString();
                    
                    return (
                      <tr key={payslip.payslip_id} className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                              <span className="text-white font-semibold text-sm">
                                {payslip.employee_name?.charAt(0) || 'U'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-slate-100">{payslip.employee_name || 'Unknown'}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">{payslip.employee_id}</p>
                              <p className="text-xs text-slate-400">{payslip.employee_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            <span className="text-sm text-slate-900 dark:text-slate-100">
                              {formatMonthYear(payslip.date)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {formatCurrency(payslip.gross_salary)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-medium text-red-600">
                            -{formatCurrency(payslip.total_deductions)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-bold text-emerald-600">
                            {formatCurrency(payslip.net_salary)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownload(payslip.payslip_id, payslip.employee_name, month, year)}
                              disabled={downloadingId === payslip.payslip_id}
                              className="h-8 border-emerald-400 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-200 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/40"
                            >
                              {downloadingId === payslip.payslip_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Download className="h-3 w-3" />
                              )}
                              <span className="ml-1 hidden sm:inline">Download</span>
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSendEmail(payslip.payslip_id, payslip.employee_name, payslip.employee_email)}
                              disabled={sendingEmail === payslip.payslip_id || !payslip.employee_email}
                              className="h-8 bg-blue-600 hover:bg-blue-700"
                              title={payslip.employee_email ? `Send to ${payslip.employee_email}` : 'No email available'}
                            >
                              {sendingEmail === payslip.payslip_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Send className="h-3 w-3" />
                              )}
                              <span className="ml-1 hidden sm:inline">Send Email</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Employee View (Original)
  const filteredGroupedPayslips = Object.keys(groupedPayslips)
    .filter(monthYear => {
      const [year, month] = monthYear.split('-');
      if (selectedYear !== 'all' && year !== selectedYear) return false;
      if (selectedMonth !== 'all' && month !== selectedMonth) return false;
      return true;
    })
    .reduce((acc, key) => {
      acc[key] = groupedPayslips[key];
      return acc;
    }, {});

  const employeeYears = [...new Set(Object.keys(groupedPayslips).map(my => my.split('-')[0]))].sort().reverse();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">My Payslips</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            View and download your monthly payslips
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option value="all">All Years</option>
              {employeeYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option value="all">All Months</option>
              <option value="01">January</option>
              <option value="02">February</option>
              <option value="03">March</option>
              <option value="04">April</option>
              <option value="05">May</option>
              <option value="06">June</option>
              <option value="07">July</option>
              <option value="08">August</option>
              <option value="09">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>
        </div>
      </div>

      {Object.keys(filteredGroupedPayslips).length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <FileText className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">No payslips available</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.keys(filteredGroupedPayslips)
            .sort((a, b) => b.localeCompare(a))
            .map((monthYear) => {
              const monthPayslips = filteredGroupedPayslips[monthYear];
              const payslip = monthPayslips[0];
              const [year, month] = monthYear.split('-');

              return (
                <div
                  key={monthYear}
                  className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-4">
                        <Calendar className="h-5 w-5 text-emerald-600" />
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                          {formatMonthYear(payslip.date)}
                        </h3>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Basic Salary</p>
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100 tabular-nums">
                            {formatCurrency(payslip.basic_salary)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Allowances</p>
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100 tabular-nums">
                            {formatCurrency(payslip.allowances)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total Deductions</p>
                          <p className="text-sm font-medium text-red-600 tabular-nums">
                            -{formatCurrency(payslip.total_deductions)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Net Salary</p>
                          <p className="text-base font-bold text-emerald-600 tabular-nums">
                            {formatCurrency(payslip.net_salary)}
                          </p>
                        </div>
                      </div>
                      
                      {payslip.claims_reimbursement > 0 && (
                        <div className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800 rounded-lg">
                          <p className="text-xs text-emerald-700 dark:text-emerald-400 mb-1">Claims Reimbursement</p>
                          <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300 tabular-nums">
                            +{formatCurrency(payslip.claims_reimbursement)}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => handleDownload(payslip.payslip_id, user.full_name, month, year)}
                      disabled={downloadingId === payslip.payslip_id}
                      className="ml-4 bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700"
                      data-testid={`download-payslip-${monthYear}`}
                    >
                      {downloadingId === payslip.payslip_id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Download
                    </Button>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
